import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Newspaper, Plus, Trash2, Loader2 } from "lucide-react";
import { getAll, create, remove } from "../../services/firestoreService";
import { updateCollectionVersion } from "../../services/versionService";
import { SEED_PRESS } from "../../data/seed";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import SeedDataButton from "./SeedDataButton";

const COLLECTION = "press";

export default function PressEditor() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [adding, setAdding] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAll(COLLECTION);
      setItems(data);
    } catch {
      toast.error("Gagal memuat data media");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const handleAdd = async () => {
    if (!newName.trim()) {
      toast.error("Nama media wajib diisi");
      return;
    }
    setAdding(true);
    try {
      await create(COLLECTION, { name: newName.trim() });
      toast.success("Media berhasil ditambahkan!");
      await updateCollectionVersion(COLLECTION);
      setNewName("");
      loadItems();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await remove(COLLECTION, deleteTarget.id);
      toast.success("Media berhasil dihapus!");
      await updateCollectionVersion(COLLECTION);
      setDeleteTarget(null);
      loadItems();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-1">
          <div className="w-1 h-6 bg-[#d4af37] rounded-full" />
          <h1 className="text-2xl font-display text-[#fdfbf7]">Press</h1>
        </div>
        <p className="text-sm text-[#a6a196] ml-4">
          Nama media yang tampil di marquee strip
        </p>
      </div>

      {/* Add new */}
      <div className="flex items-center gap-2">
        <Input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleAdd()}
          placeholder="Nama media (e.g. Bridestory)"
          className="bg-[#0a0908] border-[#d4af37]/20 text-[#fdfbf7] placeholder:text-[#a6a196]/50 max-w-sm"
        />
        <Button
          onClick={handleAdd}
          disabled={adding || !newName.trim()}
          className="bg-[#d4af37] text-[#0a0908] hover:bg-[#d4af37]/90 shrink-0"
        >
          {adding ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              <Plus className="w-4 h-4 mr-1" /> Tambah
            </>
          )}
        </Button>
        <SeedDataButton
          collectionName={COLLECTION}
          data={SEED_PRESS.map((name) => ({ name }))}
          onComplete={loadItems}
          isEmpty={items.length === 0}
        />
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-[#d4af37] animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-[#a6a196] border border-dashed border-[#d4af37]/20 rounded-lg">
          <Newspaper className="w-12 h-12 mb-3 opacity-20" />
          <p className="text-sm">Belum ada data media</p>
        </div>
      ) : (
        <div className="rounded-lg border border-[#d4af37]/10 bg-[#0a0908]">
          <div className="space-y-1 p-2">
            {items.map((item) => (
              <div
                key={item.id}
                className="flex items-center justify-between px-4 py-3 rounded-md hover:bg-[#15120f]/50 transition-colors group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[#d4af37]/10 flex items-center justify-center text-[#d4af37]">
                    <Newspaper className="w-4 h-4" />
                  </div>
                  <p className="text-sm text-[#fdfbf7] font-medium">
                    {item.name}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setDeleteTarget(item)}
                  className="h-8 w-8 text-[#a6a196] hover:text-red-400 hover:bg-red-400/10 opacity-0 group-hover:opacity-100 transition-all"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={() => setDeleteTarget(null)}
      >
        <AlertDialogContent className="bg-[#15120f] border-[#d4af37]/20 text-[#fdfbf7]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#fdfbf7]">
              Hapus Media
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#a6a196]">
              Apakah Anda yakin ingin menghapus "{deleteTarget?.name}"?
              Tindakan ini tidak dapat dibatalkan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-[#0a0908] border-[#d4af37]/20 text-[#a6a196] hover:bg-[#15120f]">
              Batal
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20"
            >
              Hapus
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
